import { useState } from 'react'
import type { SettingField, SettingValue } from '../../types/settings'
import {
  BOUNDS, effectWords, isPairList, pairHeadings, rowsOf, sectionsOf, separatorOf, sourceClass, sourceWords, tidy, toPairs,
} from './groups'

// The settings, section by section, generated from the pack manifest. Presentational only: every
// field, its label, its options, its default and its WHY come from the manifest as the API served it,
// and the only thing decided here is which control a kind gets and what order the sections read in.
//
// No hooks but local draft state, so the whole form renders in a test and in a screenshot.

export interface SectionsProps {
  fields: SettingField[]
  /** the key being saved right now, if any */
  busy: string | null
  /** the key that was refused, and the API's own words for why */
  error: { key: string; message: string } | null
  onSave: (key: string, value: SettingValue) => void
  onReset: (key: string) => void
}

export function Sections({ fields, busy, error, onSave, onReset }: SectionsProps) {
  return (
    <>
      {sectionsOf(fields).map((section) => (
        <section className="card setting-section" key={section.id}>
          <div className="card-head">
            <div>
              <h2>{section.title}</h2>
              <p className="muted">{section.blurb}</p>
            </div>
          </div>
          <ul className="plain setting-list">
            {section.fields.map((field) => (
              <li key={field.key}>
                <FieldRow
                  field={field}
                  busy={busy === field.key}
                  error={error?.key === field.key ? error.message : null}
                  onSave={onSave}
                  onReset={onReset}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

export interface FieldRowProps {
  field: SettingField
  busy: boolean
  error: string | null
  onSave: (key: string, value: SettingValue) => void
  onReset: (key: string) => void
}

/** One setting: what it is, why it exists, what it is set to, and who set it. */
export function FieldRow({ field, busy, error, onSave, onReset }: FieldRowProps) {
  const [draft, setDraft] = useState<SettingValue>(field.value)
  const readOnly = field.binding === null
  // a list is saved tidied: a row somebody added and left empty is not a change, and typing in it is
  const value: SettingValue = field.kind === 'text' ? tidy(String(draft), field.key) : draft
  const dirty = String(value) !== String(field.value)

  return (
    <div className={`setting${readOnly ? ' read-only' : ''}${dirty ? ' dirty' : ''}`}>
      <div className="setting-head">
        <label className="setting-label" htmlFor={`setting-${field.key}`}>{field.label}</label>
        <span className={sourceClass(field.source)}>{sourceWords(field)}</span>
      </div>
      {field.why && <p className="setting-why">{field.why}</p>}

      <div className="setting-control">
        {readOnly ? (
          <p className="setting-wired">
            <code>{String(field.value) || 'not set'}</code>
            <span className="muted"> — chosen when Scout was deployed. Changing it is a deploy, not a save.</span>
          </p>
        ) : (
          <Editor field={field} draft={draft} onChange={setDraft} />
        )}
      </div>

      {!readOnly && (
        <div className="setting-actions">
          <button type="button" className="button small primary" disabled={!dirty || busy} onClick={() => onSave(field.key, value)}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {dirty && (
            <button type="button" className="link" onClick={() => setDraft(field.value)}>Cancel</button>
          )}
          {field.source === 'stored' && (
            <button type="button" className="link" disabled={busy} onClick={() => onReset(field.key)}>
              Reset to deployed
            </button>
          )}
          <span className={`setting-effect ${field.effect}`}>{effectWords(field.effect)}</span>
        </div>
      )}

      {error && <p className="setting-refusal">{error}</p>}
    </div>
  )
}

function Editor({ field, draft, onChange }: { field: SettingField; draft: SettingValue; onChange: (v: SettingValue) => void }) {
  const id = `setting-${field.key}`

  if (field.kind === 'choice') {
    return (
      <select id={id} className="setting-input" value={String(draft)} onChange={(e) => onChange(e.target.value)}>
        {(field.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    )
  }

  if (field.kind === 'boolean') {
    const on = draft === true || draft === 'true'
    return (
      <span className="setting-toggle">
        <input id={id} type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
        <label htmlFor={id}>{on ? 'On' : 'Off'}</label>
      </span>
    )
  }

  if (field.kind === 'number') {
    const bounds = BOUNDS[field.key]
    return (
      <span className="setting-number">
        <input
          id={id}
          type="number"
          className="setting-input"
          value={String(draft)}
          min={bounds?.min}
          max={bounds?.max}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
        {bounds && <span className="muted small">{bounds.min}–{bounds.max}</span>}
      </span>
    )
  }

  // text: a list of values, or a list of pairs. Either way the person edits rows and the screen
  // writes the one string the setting is stored as, so nobody has to remember the separator.
  if (field.kind === 'text') {
    return isPairList(field.key)
      ? <PairRows field={field} draft={String(draft)} onChange={onChange} />
      : <ValueRows field={field} draft={String(draft)} onChange={onChange} />
  }

  return <input id={id} type="text" className="setting-input" value={String(draft)} onChange={(e) => onChange(e.target.value)} />
}

function PairRows({ field, draft, onChange }: { field: SettingField; draft: string; onChange: (v: string) => void }) {
  const pairs = toPairs(draft, field.key)
  const [left, right] = pairHeadings(field.key)
  // written back verbatim, blanks and all: tidying happens when the person presses Save
  const write = (next: { left: string; right: string }[]) =>
    onChange(next.map((p) => `${p.left}=${p.right}`).join(separatorOf(field.key)))
  return (
    <div className="setting-rows pairs">
      <div className="setting-row heads">
        <span className="muted small">{left}</span>
        <span className="muted small">{right}</span>
        <span />
      </div>
      {pairs.map((p, i) => (
        <div className="setting-row" key={`${p.left}-${i}`}>
          <input className="setting-input" value={p.left} aria-label={left}
            onChange={(e) => write(pairs.map((q, j) => (j === i ? { ...q, left: e.target.value } : q)))} />
          <input className="setting-input" value={p.right} aria-label={right}
            onChange={(e) => write(pairs.map((q, j) => (j === i ? { ...q, right: e.target.value } : q)))} />
          <button type="button" className="link" onClick={() => write(pairs.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="link" onClick={() => write([...pairs, { left: '', right: '' }])}>Add a row</button>
      {pairs.length === 0 && <p className="muted small">None yet.</p>}
    </div>
  )
}

function ValueRows({ field, draft, onChange }: { field: SettingField; draft: string; onChange: (v: string) => void }) {
  const values = rowsOf(draft, field.key)
  const write = (next: string[]) => onChange(next.join(separatorOf(field.key)))
  return (
    <div className="setting-rows">
      {values.map((v, i) => (
        <div className="setting-row single" key={`${v}-${i}`}>
          <input className="setting-input" value={v} aria-label={field.label}
            onChange={(e) => write(values.map((w, j) => (j === i ? e.target.value : w)))} />
          <button type="button" className="link" onClick={() => write(values.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="link" onClick={() => write([...values, ''])}>Add one</button>
      {values.length === 0 && <p className="muted small">None yet.</p>}
    </div>
  )
}
